import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem,
  Skeleton, Tooltip, IconButton,
} from '@mui/material';
import RestartAltIcon  from '@mui/icons-material/RestartAlt';
import AssessmentIcon  from '@mui/icons-material/Assessment';
import apiService      from '../services/apiServices';
import globals         from '../services/globals';
import { getLogedInUser } from '../services/authSession';

// Day count: up to 2 decimals, trailing zeros trimmed (e.g. 2.80 → "2.8").
const fmtDays = (n) => {
  const r = Math.round((Number(n) || 0) * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
};

function getCurrentUser() {
  // Shared reader (sessionStorage → cookie → localStorage); the old localStorage-
  // first read missed cookie/Electron sessions. See frontend_session_token.
  const u = getLogedInUser() || {};
  return u?.data || u;
}

const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR + 1, THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2, THIS_YEAR - 3];

export default function MyLeaveReportPage() {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const myWorkerId  = currentUser?.worker_id || currentUser?.id || currentUser?.Id;
  const myName      = [currentUser?.first_name || currentUser?.FirstName, currentUser?.last_name || currentUser?.LastName]
                        .filter(Boolean).join(' ') || currentUser?.name || currentUser?.username || 'Unë';
  const myNumber    = currentUser?.personal_number || currentUser?.PersonalNumber
                        || currentUser?.nid || currentUser?.number || '—';

  const [year, setYear]               = useState(THIS_YEAR);
  const [holidays, setHolidays]       = useState([]);
  const [annual, setAnnual]           = useState(20);
  const [carriedOver, setCarriedOver] = useState(0);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiService().get('holidays'),
      globals().getSettings().catch(() => null),
    ]).then(async ([res, settings]) => {
      if (cancelled) return;
      if (res.success) {
        const mine = (res.data || []).filter(h =>
          Number(h.WorkerId) === Number(myWorkerId) &&
          new Date(h.StartDate).getFullYear() === year
        );
        setHolidays(mine);
      }
      if (settings) setAnnual(Number(settings.AnnualLeaveAllowance) || 20);

      // Carried-over leave (Bartur) lives in leave_balances; fetch separately.
      let co = 0;
      if (myWorkerId) {
        try {
          const lb = await apiService().query('getLeaveBalances', `workerId=${myWorkerId}&year=${year}`);
          if (lb?.success) {
            co = (lb.data || [])
              .filter(b => Number(b.LeaveTypeId) === 1)
              .reduce((s, b) => s + (Number(b.CarriedOver) || 0), 0);
          }
        } catch { /* fall back to 0 */ }
      }
      if (!cancelled) { setCarriedOver(co); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [year, myWorkerId]);

  const row = useMemo(() => {
    // 3 dhjetore — mbledhja/zbritja float e gjysmë-ditëve lë artefakte (2.5999…).
    const r3 = (n) => Math.round(n * 1000) / 1000;
    const isApproved = (h) => (h.Status ? h.Status === 'approved' : !!h.Approve);
    const sumApproved = (types) => r3(holidays
      .filter(h => isApproved(h) && types.includes(Number(h.HolidayType)))
      .reduce((s, h) => s + (Number(h.holidayDays || h.HolidayDays) || 0), 0));

    const usedAnnualTotal = sumApproved([1, 6]);             // vjetor + me pagesë
    const usedCarried     = Math.min(usedAnnualTotal, carriedOver);
    const usedAnnual      = r3(usedAnnualTotal - usedCarried);
    const capacity        = r3(annual + carriedOver);
    const remaining       = Math.max(0, r3(capacity - usedAnnualTotal));

    // Carried-over leave conventionally expires 30 June. Unused-after-expiry is lost.
    const expiry          = new Date(year, 5, 30, 23, 59, 59);
    const remainingCarried = Math.max(0, r3(carriedOver - usedCarried));
    const lostCarried     = (new Date() > expiry) ? remainingCarried : 0;

    return {
      total: capacity,
      annual,
      usedAnnual,
      carriedOver,
      usedCarried,
      lostCarried,
      totalUsed: usedAnnualTotal,
      remaining,
    };
  }, [holidays, annual, carriedOver, year]);

  const thSx = {
    fontWeight: 700, fontSize: '0.7rem', py: 1, px: 1.5,
    color: '#6B7280', bgcolor: '#F9FAFB',
    letterSpacing: '0.03em', whiteSpace: 'nowrap',
    borderBottom: '1px solid #E5E7EB',
  };
  const tdSx = { py: 1, px: 1.5, fontSize: '0.85rem', color: '#374151', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' };
  const numSx = { ...tdSx, fontWeight: 700, fontVariantNumeric: 'tabular-nums' };

  const COLS = [
    { key: 'number',     label: 'Numri',                        render: () => myNumber },
    { key: 'name',       label: 'Emri dhe mbiemri',             render: () => myName },
    { key: 'type',       label: 'Lloji i pushimit',             render: () => 'Pushim vjetor' },
    { key: 'year',       label: 'Viti',                         render: () => year },
    { key: 'total',      label: 'Total',                        num: true, render: () => fmtDays(row.total) },
    { key: 'annual',     label: 'Ditët e pushimit',             num: true, render: () => fmtDays(row.annual) },
    { key: 'used',       label: 'Ditët e shpenzuara',           num: true, render: () => fmtDays(row.usedAnnual) },
    { key: 'carried',    label: 'Ditët e bartura',              num: true, render: () => fmtDays(row.carriedOver) },
    { key: 'usedCar',    label: 'Ditët e shpenzuara (bartura)', num: true, render: () => fmtDays(row.usedCarried) },
    { key: 'lostCar',    label: 'Ditët e humbura (bartura)',    num: true, render: () => fmtDays(row.lostCarried) },
    { key: 'totalUsed',  label: 'Total ditë të shpenzuara',     num: true, render: () => fmtDays(row.totalUsed) },
    { key: 'remaining',  label: 'Ditët e mbetura',              num: true, render: () => fmtDays(row.remaining) },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1F2937' }}>
        Raporti i pushimeve
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, alignItems: 'flex-start' }}>

        {/* Filters */}
        <Paper elevation={0} sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>Filterat</Typography>
            <Tooltip title="Rivendos">
              <IconButton size="small" onClick={() => setYear(THIS_YEAR)} sx={{ width: 26, height: 26 }}>
                <RestartAltIcon sx={{ fontSize: 16, color: '#6B7280' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ p: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Viti</InputLabel>
              <Select label="Viti" value={year} onChange={e => setYear(Number(e.target.value))} sx={{ fontSize: '0.85rem' }}>
                {YEARS.map(y => <MenuItem key={y} value={y} sx={{ fontSize: '0.85rem' }}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Report table */}
        <Paper elevation={0} sx={{ flex: 1, minWidth: 0, width: '100%', border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ fontSize: 18, color: '#6366F1' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>
              Gjendja momentale e pushimeve për punëtor
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLS.map(c => (
                    <TableCell key={c.key} sx={{ ...thSx, textAlign: c.num ? 'right' : 'left' }}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    {COLS.map((c, j) => (
                      <TableCell key={j} sx={c.num ? numSx : tdSx} align={c.num ? 'right' : 'left'}>
                        <Skeleton width={c.num ? 28 : 80} height={16} sx={{ ml: c.num ? 'auto' : 0 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : !myWorkerId ? (
                  <TableRow>
                    <TableCell colSpan={COLS.length} align="center" sx={{ py: 5, color: '#6B7280', fontSize: '0.85rem' }}>
                      Llogaria nuk është e lidhur me një punëtor.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow hover sx={{ '&:hover td': { bgcolor: '#FAFAFA' } }}>
                    {COLS.map(c => (
                      <TableCell key={c.key} sx={c.num ? numSx : tdSx} align={c.num ? 'right' : 'left'}>
                        {c.render()}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}
